import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  LogOut, Users, Trophy, Calendar, MapPin, FileSpreadsheet, 
  Plus, Edit, Shield, Menu, X, Award
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import CategoriasTab from '@/components/admin/CategoriasTab';
import ClubesTab from '@/components/admin/ClubesTab';
import FechasTab from '@/components/admin/FechasTab';
import UsuariosTab from '@/components/admin/UsuariosTab';
import ConsultaIdentidad from '@/components/ConsultaIdentidad';
import TorneosSection from '@/components/admin/TorneosSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard({ user }) {
  const [ligas, setLigas] = useState([]);
  const [selectedLiga, setSelectedLiga] = useState(null);
  const [showLigaDialog, setShowLigaDialog] = useState(false);
  const [editingLiga, setEditingLiga] = useState(null);
  const [newLigaName, setNewLigaName] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('torneos');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => { loadLigas(); }, []);

  const loadLigas = async () => {
    try {
      const response = await axios.get(`${API}/ligas`, { withCredentials: true });
      setLigas(response.data);
      if (response.data.length > 0 && !selectedLiga) {
        setSelectedLiga(response.data[0]);
      }
    } catch (error) {
      toast.error('Error al cargar ligas');
    }
  };

  const handleSaveLiga = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingLiga) {
        const response = await axios.put(`${API}/ligas/${editingLiga.id}`, { name: newLigaName }, { withCredentials: true });
        toast.success('Liga actualizada');
        setSelectedLiga(response.data);
      } else {
        const response = await axios.post(`${API}/ligas`, { name: newLigaName }, { withCredentials: true });
        toast.success('Liga creada');
        setSelectedLiga(response.data);
      }
      setShowLigaDialog(false);
      setEditingLiga(null);
      setNewLigaName('');
      await loadLigas();
    } catch (error) {
      toast.error(editingLiga ? 'Error al actualizar liga' : 'Error al crear liga');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      localStorage.removeItem('session_token');
      toast.success('Sesion cerrada');
      window.location.href = '/ligas';
    } catch (error) {
      toast.error('Error al cerrar sesion');
    }
  };

  const handleSetSection = (section) => {
    setActiveSection(section);
    setSidebarOpen(false);
  };

  const NAV_ITEMS = [
    { id: 'torneos', label: 'Torneos', icon: Award },
    { id: 'categorias', label: 'Categorias', icon: MapPin },
    { id: 'clubes', label: 'Clubes', icon: FileSpreadsheet },
    { id: 'fechas', label: 'Fechas', icon: Calendar },
    { id: 'identidad', label: 'Identidad', icon: Shield },
    { id: 'usuarios', label: 'Usuarios', icon: Users },
  ];

  // Empty state - no ligas
  if (!selectedLiga && ligas.length === 0) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Card className="p-8 text-center max-w-md bg-slate-800 border-slate-700" data-testid="no-liga-message">
          <Trophy className="w-16 h-16 text-slate-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Sin Ligas</h2>
          <p className="text-slate-400 mb-6">Crea tu primera liga para comenzar</p>
          <Button
            data-testid="create-first-liga-btn"
            onClick={() => setShowLigaDialog(true)}
            className="gap-2 bg-white text-slate-900 hover:bg-slate-200"
          >
            <Plus className="w-4 h-4" />
            Crear Primera Liga
          </Button>
        </Card>

        <Dialog open={showLigaDialog} onOpenChange={setShowLigaDialog}>
          <DialogContent className="bg-slate-800 border-slate-700" data-testid="liga-dialog">
            <DialogHeader>
              <DialogTitle className="text-white">Nueva Liga</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSaveLiga} className="space-y-4">
              <div>
                <Label className="text-slate-300">Nombre de la Liga</Label>
                <Input
                  data-testid="liga-name-input"
                  value={newLigaName}
                  onChange={(e) => setNewLigaName(e.target.value)}
                  required
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <Button data-testid="liga-save-btn" type="submit" className="w-full bg-white text-slate-900 hover:bg-slate-200" disabled={loading}>
                {loading ? 'Creando...' : 'Crear Liga'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col" data-testid="admin-dashboard">
      {/* Top Bar */}
      <header className="h-16 bg-slate-800 border-b border-slate-700 flex items-center justify-between px-4 shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
            data-testid="sidebar-toggle"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <img src="/kinosis-logo.png" alt="Kinosis" className="h-8 sm:h-10 w-auto" />
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-slate-900 font-bold text-sm">
              {user.name?.charAt(0) || user.email?.charAt(0)}
            </div>
            <div className="hidden sm:block text-right">
              <p className="text-sm font-medium text-white">{user.name || user.email}</p>
              <p className="text-xs text-slate-400">{user.email}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-400 hover:text-white hover:bg-slate-700" data-testid="logout-btn">
            <LogOut className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Cerrar Sesion</span>
          </Button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden relative">
        {/* Mobile overlay */}
        {sidebarOpen && (
          <div className="lg:hidden fixed inset-0 bg-black/50 z-20" onClick={() => setSidebarOpen(false)} />
        )}

        {/* Sidebar */}
        <aside className={`
          fixed lg:relative inset-y-0 left-0 z-30
          w-64 bg-slate-800 border-r border-slate-700 flex flex-col
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          lg:transform-none lg:shrink-0
          pt-16 lg:pt-0
        `}>
          {/* Liga Selector */}
          {ligas.length > 1 && (
            <div className="p-3 border-b border-slate-700">
              <Select value={selectedLiga?.id} onValueChange={(id) => setSelectedLiga(ligas.find(l => l.id === id))}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="liga-selector">
                  <div className="flex items-center gap-2 truncate">
                    <Trophy className="w-4 h-4 text-white shrink-0" />
                    <span className="truncate">{selectedLiga?.name}</span>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {ligas.map((liga) => (
                    <SelectItem key={liga.id} value={liga.id}>{liga.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Liga Header */}
          <div className="p-4 border-b border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-white flex items-center justify-center">
                <Trophy className="w-6 h-6 text-slate-900" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-bold text-white truncate">{selectedLiga?.name}</h2>
                <p className="text-xs text-slate-400">Liga Deportiva</p>
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <Button
                size="sm" variant="ghost"
                className="flex-1 text-xs text-slate-400 hover:text-white hover:bg-slate-700 gap-1"
                onClick={() => {
                  setEditingLiga(selectedLiga);
                  setNewLigaName(selectedLiga?.name || '');
                  setShowLigaDialog(true);
                }}
                data-testid="edit-liga-btn"
              >
                <Edit className="w-3 h-3" /> Editar
              </Button>
              <Button
                size="sm" variant="ghost"
                className="flex-1 text-xs text-slate-400 hover:text-white hover:bg-slate-700 gap-1"
                onClick={() => { setEditingLiga(null); setNewLigaName(''); setShowLigaDialog(true); }}
                data-testid="add-liga-btn"
              >
                <Plus className="w-3 h-3" /> Nueva
              </Button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {NAV_ITEMS.map(item => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleSetSection(item.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    isActive ? 'bg-white text-slate-900' : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                  }`}
                  data-testid={`nav-${item.id}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          {activeSection === 'torneos' && (
            <TorneosSection ligaId={selectedLiga?.id} />
          )}

          {activeSection === 'categorias' && (
            <CategoriasTab ligaId={selectedLiga?.id} ligaName={selectedLiga?.name} />
          )}

          {activeSection === 'clubes' && (
            <ClubesTab ligaId={selectedLiga?.id} ligaName={selectedLiga?.name} />
          )}

          {activeSection === 'fechas' && (
            <FechasTab user={user} ligaId={selectedLiga?.id} ligaName={selectedLiga?.name} />
          )}

          {activeSection === 'identidad' && (
            <ConsultaIdentidad mode="liga" />
          )}

          {activeSection === 'usuarios' && (
            <UsuariosTab user={user} />
          )}
        </main>
      </div>

      {/* Liga Dialog */}
      <Dialog open={showLigaDialog} onOpenChange={setShowLigaDialog}>
        <DialogContent className="bg-slate-800 border-slate-700" data-testid="liga-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">{editingLiga ? 'Editar Liga' : 'Nueva Liga'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveLiga} className="space-y-4">
            <div>
              <Label className="text-slate-300">Nombre de la Liga</Label>
              <Input
                data-testid="liga-name-input"
                value={newLigaName}
                onChange={(e) => setNewLigaName(e.target.value)}
                required
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <Button data-testid="liga-save-btn" type="submit" className="w-full bg-white text-slate-900 hover:bg-slate-200" disabled={loading}>
              {loading ? 'Guardando...' : (editingLiga ? 'Actualizar Liga' : 'Crear Liga')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
